import { useEffect, useState } from "react";
import { X, PaperPlaneTilt } from "@phosphor-icons/react";
import { sendFeedback } from "../lib/api";
import { toast } from "sonner";

/**
 * Lightweight modal — no shadcn dependency, matches the editorial-brutalism palette.
 * Triggered from the Sidebar "Send feedback" link.
 */
export const FeedbackDialog = ({ open, onClose }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await sendFeedback(trimmed, window.location.pathname);
      toast("Thank you — the studio is listening.", {
        description: "Every note nudges the next iteration.",
      });
      setText("");
      onClose();
    } catch (err) {
      toast.error("Couldn't send feedback. Try again in a minute.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
      data-testid="feedback-dialog"
      role="dialog"
      aria-modal="true"
    >
      <button
        aria-label="close"
        onClick={onClose}
        className="absolute inset-0 bg-ink/40 backdrop-blur-[2px]"
        data-testid="feedback-dialog-overlay"
      />
      <div className="relative w-full max-w-lg bg-surface border border-line rounded-2xl shadow-[0_12px_40px_rgba(28,26,22,0.18)]">
        <header className="flex items-baseline justify-between border-b border-line px-6 py-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[1.3px] text-amber">
              note to studio
            </p>
            <h2 className="font-serif text-2xl mt-1">Send feedback</h2>
          </div>
          <button
            onClick={onClose}
            className="text-ink-dim hover:text-ink"
            data-testid="feedback-close-btn"
            aria-label="close feedback"
          >
            <X size={18} weight="thin" />
          </button>
        </header>

        <form onSubmit={submit} className="px-6 py-5">
          <label
            htmlFor="feedback-textarea"
            className="block text-[10px] uppercase tracking-[0.14em] text-ink-dim"
          >
            What's working, what's not, what's missing
          </label>
          <textarea
            id="feedback-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            maxLength={4000}
            placeholder="Honest, sharp, anything — the agent reads everything."
            className="mt-2 w-full border border-line bg-surface-hi/60 rounded-lg px-3 py-2 font-serif text-base leading-relaxed focus:outline-none focus:border-amber resize-none"
            autoFocus
            data-testid="feedback-textarea"
          />
          <div className="mt-1 flex items-center justify-between text-[10px] uppercase tracking-[0.14em] text-ink-dim">
            <span>signed with your studio name &amp; email</span>
            <span>{text.length} / 4000</span>
          </div>

          <div className="mt-5 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[11px] uppercase tracking-[0.14em] text-ink-dim hover:text-ink"
              data-testid="feedback-cancel-btn"
            >
              cancel
            </button>
            <button
              type="submit"
              disabled={busy || !text.trim()}
              className="inline-flex items-center gap-2 px-5 py-2 bg-amber text-[#20170a] rounded-full text-[12px] font-semibold hover:bg-amber-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="feedback-submit-btn"
            >
              <PaperPlaneTilt size={12} weight="bold" />
              {busy ? "sending…" : "send"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
